import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { Button } from 'reactstrap/lib'
import {
  ChannelPreviewActionButtons as ChallengedChannelPreviewActionButtons,
  useChannelMembershipState,
  useTranslationContext,
} from 'stream-chat-react'
import type { User } from 'stream-chat/dist/types'
import { archiveChannel, unarchiveChannel } from './actions'
import ChatModal from './ChatModal'

import './ChannelPreviewActionButtons.scss'
import './ChatModal.scss'

const ChannelPreviewActionButtons = ({ channel }: Parameters<typeof ChallengedChannelPreviewActionButtons>[0]) => {
  const [modalOpen, setModalOpen] = React.useState(false)
  const membership = useChannelMembershipState(channel);
  const { t } = useTranslationContext();

  // Enable the archive button for channel owners and admins
  const archiveEnabled = React.useMemo(() => (
    (channel.data?.created_by as User)?.id === membership.user?.id || membership.user?.role === 'admin'
  ), [channel.data?.created_by, membership.user?.id, membership.user?.role])

  // Only show archive button for non-channel-owners and non-admins if the channel is archived
  const archiveVisible = React.useMemo(() => (
    !!channel.data?.readOnly || archiveEnabled
  ), [channel.data?.readOnly, archiveEnabled])

  return (
    <div className='chat-channel-preview-action-buttons'>
      {archiveVisible && (
        <button
          disabled={!archiveEnabled}
          className='chat-channel-preview-action-buttons__archive'
          aria-label={channel.data?.readOnly ? t('Unarchive') : t('Archive')}
          onClick={async (e) => {
            e.stopPropagation()
            if (channel.data?.readOnly) {
              unarchiveChannel(channel.id!)
              return
            }
            setModalOpen(true)
          }}
          title={channel.data?.readOnly ? t('Unarchive') : t('Archive')}
        >
          <FontAwesomeIcon icon={[channel.data?.readOnly ? 'fas' : 'fal', 'archive']} size='sm' />
        </button>
      )}
      {modalOpen && (
        <ChatModal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
          <div>
            <h3>Are you sure you want to archive this channel?</h3>
            <p>The channel will become read-only and all messages and data will be backed up.</p>
            <div className='d-flex justify-content-end'>
              <Button color='secondary' outline onClick={() => setModalOpen(false)}>
                <FontAwesomeIcon icon={['fal', 'times']} fixedWidth /> Cancel
              </Button>
              <Button color='danger' className='ml-3' onClick={() => {
                archiveChannel(channel.id!)
                  .finally(() => setModalOpen(false))
              }}>
                <FontAwesomeIcon icon={['fal', 'trash']} /> Confirm Archive
              </Button>
            </div>
          </div>
        </ChatModal>
      )}
    </div>
  )
}

export default ChannelPreviewActionButtons
